// feature/despacho-documents — Documentos del Despacho.
// Shared documents (poderes, formatos, plantillas). All authenticated users
// can view + download; admin can upload + delete.
import React, { useEffect, useRef, useState } from "react";
import { despacho as despachoApi } from "../api/endpoints";
import { apiBase } from "../api/client";
import Spinner from "../components/Spinner";
import Empty from "../components/Empty";
import Modal from "../components/Modal";
import Confirm from "../components/Confirm";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../components/Toast";
import { rolesOf } from "../utils/permissions";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";

export default function Despacho() {
  const { user } = useAuth();
  const toast = useToast();
  // feature/single-role-audit — multi-role aware admin check.
  const isAdmin = rolesOf(user).includes("admin");
  const [data, setData] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  function load() {
    setData(null);
    despachoApi.list().then(r => setData(r.data || [])).catch(() => setData([]));
  }
  useEffect(() => { load(); }, []);

  async function doDelete() {
    try {
      await despachoApi.remove(confirmDel.id);
      toast.push("Documento eliminado");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    }
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="row" style={{ alignItems: "center" }}>
        <div>
          <h1 style={{ margin: 0 }}>Documentos del Despacho</h1>
          <div className="muted" style={{ marginTop: 4 }}>
            Poderes, formatos y plantillas compartidos por el equipo.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <button onClick={() => setUploadOpen(true)}>Subir documento</button>
        )}
      </div>

      {data == null ? (
        <div className="card" style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
      ) : !data.length ? (
        <Empty title="Sin documentos todavía" message="Cuando un administrador suba documentos, aparecerán aquí." />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          {data.map(d => (
            <div key={d.id} className="card stat-card">
              <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{d.name}</div>
              {d.category && (
                <div className="muted" style={{ fontSize: 11, marginTop: 4, textTransform: "uppercase", letterSpacing: ".06em" }}>{d.category}</div>
              )}
              {d.description && (
                <div style={{ fontSize: 13, marginTop: 8, color: "var(--text-body)" }}>{d.description}</div>
              )}
              <div className="muted" style={{ fontSize: 11, marginTop: 12 }}>
                Subido por {d.uploaded_by_name || "—"} · {fmt(d.uploaded_at)}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <a href={`${apiBase}${despachoApi.downloadUrl(d.id)}`} target="_blank" rel="noreferrer">
                  <button className="secondary sm">Descargar</button>
                </a>
                {isAdmin && (
                  <button className="ghost sm" style={{ color: "var(--color-danger)" }} onClick={() => setConfirmDel(d)}>Eliminar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} />
      )}

      <Confirm
        open={!!confirmDel}
        title="Eliminar documento"
        message={confirmDel ? `¿Eliminar "${confirmDel.name}" del despacho? Se puede recuperar desde la base de datos si fue un error.` : ""}
        onClose={() => setConfirmDel(null)}
        onConfirm={doDelete}
        confirmText="Eliminar"
        danger
      />
    </div>
  );
}

function UploadModal({ open, onClose, onUploaded }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  async function submit() {
    if (!file) { toast.push("Adjunte un archivo", "error"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (name) fd.append("name", name);
      if (description) fd.append("description", description);
      if (category) fd.append("category", category);
      await despachoApi.upload(fd);
      toast.push("Documento subido");
      onUploaded?.();
      onClose();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al subir", "error");
    } finally { setBusy(false); }
  }

  return (
    <Modal
      open={open}
      title="Subir documento al despacho"
      onClose={busy ? undefined : onClose}
      footer={<>
        <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
        <button disabled={busy || !file} onClick={submit}>{busy && <Spinner color="currentColor" />} Subir</button>
      </>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label>Nombre (opcional)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Por defecto: nombre del archivo" />
        </div>
        <div>
          <label>Categoría (opcional)</label>
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Poder, formato, plantilla, etc." />
        </div>
        <div>
          <label>Descripción</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <label>Archivo</label>
          <input ref={inputRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
      </div>
    </Modal>
  );
}
